import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Mail, Phone, Building2, Shield, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const { getAuthHeader, user: authUser } = useAuthStore();

  useEffect(() => {
    axios.get(`${API}/users/me`, { headers: getAuthHeader() })
      .then(res => {
        setProfile(res.data);
        setForm({ full_name: res.data.full_name || '', phone: res.data.phone || '' });
      })
      .catch(() => toast.error('Erro ao carregar perfil'))
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, form, { headers: getAuthHeader() });
      toast.success('Perfil atualizado');
      setProfile({ ...profile, ...form });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Senhas não conferem');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { headers: getAuthHeader() });
      toast.success('Senha alterada');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const initials = profile?.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-semibold">{profile?.full_name}</h2>
              <p className="text-sm text-muted-foreground">{profile?.email}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Shield className="h-3 w-3" />{profile?.role === 'admin' ? 'Administrador' : 'Usuário'}
                </span>
                {profile?.company_name && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />{profile.company_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4" />Informações Pessoais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Nome Completo</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={profile?.email || ''} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">O email não pode ser alterado</p>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Telefone / Celular</Label>
            <Input placeholder="+55 (11) 99999-9999" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" />
            <p className="text-xs text-muted-foreground">Usado para contato em escalas OnCall e notificações</p>
          </div>
          <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar Alterações
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Lock className="h-4 w-4" />Alterar Senha</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Senha Atual</Label>
            <Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} data-testid="current-password-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nova Senha</Label>
              <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirmar Nova Senha</Label>
              <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} data-testid="confirm-password-input" />
            </div>
          </div>
          <Button variant="outline" onClick={handleChangePassword} disabled={saving || !passwordForm.current_password || !passwordForm.new_password} data-testid="change-password-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Alterar Senha
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
